import React, { useState, FunctionComponent, SyntheticEvent } from 'react';
import classnames from 'classnames';
import moment from 'moment';

import { Attachment } from '@api/v4/attachments/attachmentTypes';
import { PrimaryButton, TertiaryButton } from '@components/library/button/index';
import { getFilenameSansExt, handleThumbnailError } from '@components/asset/modal/tabs/edit/helpers';
import { keyPressHelper } from '@helpers/accessibility';
import formatNumber from '@helpers/format_number';
import { getMomentLocale } from '@helpers/locale';

interface AdditionalAttachmentFields {
  attachment_key: string;
  created_at: string;
  id: number;
  thumbnailed: boolean;
  color_space?: string;
}

export type FullAttachment = Attachment & AdditionalAttachmentFields;

interface AssetAttachmentProps {
  assetKey: string;
  attachment: FullAttachment;
  selectedAttachmentId: number;
  setSelectedAttachmentId: SetStateDispatch<number>;
}


const ThumbnailImage = (props: { extension: string; filename: string; url: string }): JSX.Element => (
  <div className="attachment-item--thumbnail">
    <img
      alt={props.filename}
      className="attachment-item--thumbnail__img"
      onError={(event: SyntheticEvent<HTMLImageElement, Event>): void => {
        handleThumbnailError(
          event,
          props.extension,
          getFilenameSansExt(props.filename, props.extension)
        );
      }}
      src={props.url}
    />
  </div>
);

const AssetAttachment: FunctionComponent<AssetAttachmentProps> = ({
  assetKey,
  attachment,
  selectedAttachmentId,
  setSelectedAttachmentId
}) => {
  const [advancedOptionsOpen, setAdvancedOptionsOpen] = useState(false);
  const momentLocale = getMomentLocale(BFG.locales.staticSiteLocale);
  const toggleAdvancedOptionsOpen = (): void => setAdvancedOptionsOpen((prevState) => !prevState);
  const downloadLink = (): string => {
    if (!BFG.resource) return '';
    return `/${BFG.resource.slug}/attachments/${attachment.attachment_key}?dl=true&resource_key=${BFG.resource.key}&resource_type=${BFG.resource.type}`;
  };
  const trackAttachmentDownload = (): void => {
    const options = { resource_type: BFG.resource?.type, resource_key: BFG.resource?.key };
    Insight.createEvent('downloaded', assetKey, 'asset', options);
    if (options.resource_type === 'share_manifest') {
      Insight.createEvent('downloaded', assetKey, 'asset');
    }
  };
  const handleDownload = (): void => {
    trackAttachmentDownload();
    window.location.href = downloadLink();
  };
  return (
    <div
      className={classnames(
        "asset-attachment",
        { 'attachment-is-selected': attachment.id === selectedAttachmentId }
      )}
      onClick={(): void => setSelectedAttachmentId(attachment.id)}
      onKeyPress={keyPressHelper((): void => setSelectedAttachmentId(attachment.id))}
      tabIndex={0}
      role="button"
    >
      <div className="asset-attachment--top-container">
        <ThumbnailImage
          extension={attachment.extension}
          filename={attachment.filename}
          url={attachment.thumbnail_url || attachment.extension
            ? `//static-fast.brandfolder.com/assets/types/${attachment.extension}.svg`
            : `//static-fast.brandfolder.com/assets/types/file.svg`}
        />
        <div className="asset-attachment--top-buttons">
          <TertiaryButton
            onClick={(): void => {
              console.log("TODO - advanced download options");
              toggleAdvancedOptionsOpen();
            }}
          >
            {advancedOptionsOpen ? 'X' : t("Options")}
          </TertiaryButton>
          <PrimaryButton
            className="attachment-download-button"
            icon="bff-download"
            onClick={(): void => {
              if (BFG.downloadAlertEnabled) {
                BF.fx.dispatchWindowEvent('launchDownloadAlert', null, {
                  downloadCallback: handleDownload
                });
              } else {
                handleDownload();
              }
            }}
          />
        </div>
      </div>
      <div className="asset-attachment--bottom-container">
        <div>{attachment.filename}</div>
        <div className="asset-attachment--metadata-container">
          <span className="file-type-pill">{attachment.extension}</span>
          <span>{formatNumber(attachment.size)}</span>
          {attachment.width && attachment.height && (
            <span><span>&middot;</span>{attachment.width} x {attachment.height}</span>
          )}
          {attachment.color_space && (
            <span><span>&middot;</span>{attachment.color_space}</span>
          )}
          <span><span>&middot;</span>{moment(attachment.created_at).locale(momentLocale).format('L')}</span>
        </div>
      </div>
    </div>
  );
};

export default AssetAttachment;
